import { Dispatch, useEffect, useReducer, useState } from 'react'
import { PartialBrief } from '../../../Types/Brief'
import { useGlobalAuth } from '@headbox-ui/auth-hook'
import getBriefUserAttributes from '../../../Utilities/User/getBriefUserAttributes'
import { getStoredBrief } from '../../../Utilities/Storage/brief'
import useLocationAttributes from './Location/useLocationAttributes'
import getPermittedBriefParams, { BriefQueryAttributes } from './QueryParams'
import briefReducer, { BriefDispatch } from './Reducer'
import { GeocodeResponseError } from '../../../Utilities/Location/GoogleAPI/fetchGeocoderLocations'
import { useSupportedCurrenciesQuery } from '../../../Views/FormSteps/Budget/Budget.generated'
import throwGQLError from '../../../Utilities/Urql/Errors/throwGQLError'
import { Currency, SupportedCurrencyCode } from '../../../Types/generated-types'
import isBizBrief from '../../../Logic/Form/Sequences/Biz/isBizBrief'
import { useGetCompanyQuery } from '../../../Utilities/User/GetUserCompany.generated'

export interface BriefState {
  brief: PartialBrief
  loading: boolean
  dispatchBrief: Dispatch<BriefDispatch>
  geocodeError?: GeocodeResponseError
  supportedCurrencies: Currency[]
}

const useBrief = (): BriefState => {
  const { user, loading: actorLoading } = useGlobalAuth()
  const [{ data: currenciesData, fetching: currenciesFetching, error: currenciesError }] =
    useSupportedCurrenciesQuery()

  const [{ data: userCompanyData, fetching: userCompanyFetching, error: userCompanyError }] =
    useGetCompanyQuery({
      pause: !user?.companyID,
      variables: { id: user?.companyID ?? 0 },
    })
  const [queryParams] = useState<URLSearchParams>(new URLSearchParams(window.location.search))
  const { lat, lon, location, ...queryAttrs }: BriefQueryAttributes =
    getPermittedBriefParams(queryParams)
  const { fetchLoading, locationAttrs, geocodeError } = useLocationAttributes({
    lat,
    lon,
    location,
  })
  const [loading, setLoading] = useState(
    actorLoading || fetchLoading || currenciesFetching || userCompanyFetching
  )
  const briefUserAttrs = getBriefUserAttributes(user)
  const storedBriefAttrs = getStoredBrief()

  const [brief, dispatchBrief] = useReducer(briefReducer, {
    numonce: Math.floor(Math.random() * 1000000000),
    ...storedBriefAttrs,
    ...locationAttrs,
    ...queryAttrs,
    ...briefUserAttrs,
  })

  useEffect(() => {
    // Set the shared loading state manually to ensure fetched data is appended to the brief
    // state *before* brief dependent components are rendered
    if (actorLoading || fetchLoading || currenciesFetching || userCompanyFetching)
      return setLoading(true)

    setLoading(false)
  }, [actorLoading, fetchLoading, currenciesFetching, userCompanyFetching])

  useEffect(() => {
    if (actorLoading) return

    dispatchBrief({ updateAttributes: briefUserAttrs })
  }, [actorLoading])

  useEffect(() => {
    if (actorLoading || userCompanyFetching || !userCompanyData?.getCompany.name) return
    if (user?.companyName) return
    if (brief.guest?.company === userCompanyData.getCompany.name) return

    dispatchBrief({
      updateAttributes: { guest: { company: userCompanyData.getCompany.name } },
    })
  }, [actorLoading, userCompanyFetching, userCompanyData, brief.guest?.company])

  useEffect(() => {
    if (fetchLoading) return

    const { currency } = queryAttrs
    const { currency: locationCurrency, ...rest } = locationAttrs || {}

    dispatchBrief({
      updateAttributes: {
        ...(isBizBrief(brief)
          ? {
              ...rest,
              currency: brief.currency
                ? brief.currency
                : (userCompanyData?.getCompany.currency as SupportedCurrencyCode),
            }
          : { currency: locationCurrency, ...rest }), // if the location fetch fails, all location coordinates will be undefined
        ...(currency ? { currency } : {}), // ensure any currency query-param takes precedent
      },
    })
  }, [fetchLoading])

  if (currenciesError) throwGQLError(currenciesError)
  if (userCompanyError) throwGQLError(userCompanyError)

  if (currenciesData && !currenciesData.supportedCurrencies.length)
    throw new Error('No currencies returned')

  return {
    brief,
    // `loading` state variable lags behind `actorLoading` and `fetchLoading`
    loading: actorLoading || fetchLoading || loading,
    supportedCurrencies: currenciesData?.supportedCurrencies || [],
    dispatchBrief,
    geocodeError,
  }
}

export default useBrief
